import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuthStore } from '../store/authStore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const formatINR = (v) => {
  if (v === null || v === undefined) return '0';
  return Number(v).toLocaleString('en-IN');
};

const formatPct = (v) => {
  if (!v) return '0%';
  return `${parseFloat(Number(v).toFixed(2))}%`;
};

const PRODUCT_CATEGORIES = [
  'Mattresses', 'Founders', 'Bed', 'Recliner', 'Pillow', 'Comforter',
  'Throw', 'Mattress Topper', 'Sheet & Cover', 'Mattress Protector',
  'Mattress Cover', 'Weighted Blanket', 'Pillow Cases', 'Mobility', 'Dog Bed', 'Others'
];

// Inject CSS for hover effects, animations, and transitions
const EOD_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
.eod-root { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.eod-root * { box-sizing: border-box; }
.eod-card { background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06); overflow: hidden; margin-bottom: 16px; transition: box-shadow 0.25s ease, transform 0.25s ease; border: 1px solid rgba(0,0,0,0.04); }
.eod-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.06); }
.eod-table tbody tr { transition: background 0.15s ease; }
.eod-table tbody tr:hover { background: #f0f7ff !important; }
.eod-table tbody tr.eod-total-row:hover { background: #f5f5f5 !important; }
.eod-stat-card { transition: transform 0.2s ease, box-shadow 0.2s ease; cursor: default; }
.eod-stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.12) !important; }
.eod-btn { transition: all 0.2s ease !important; }
.eod-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); filter: brightness(1.05); }
.eod-btn:active { transform: translateY(0); }
.eod-tab { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important; }
.eod-tab:hover { filter: brightness(1.08); }
@keyframes eod-fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.eod-animate { animation: eod-fadeIn 0.35s ease-out; }
@keyframes eod-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
.eod-loading-pulse { animation: eod-pulse 1.5s ease-in-out infinite; }
@keyframes eod-shimmer { 0% { background-position: -200px 0; } 100% { background-position: calc(200px + 100%) 0; } }
.eod-section-header { position: relative; overflow: hidden; }
.eod-section-header::after { content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 100px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1)); pointer-events: none; }
.eod-input { transition: border-color 0.2s, box-shadow 0.2s; outline: none; }
.eod-input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.12); }
.eod-scroll::-webkit-scrollbar { height: 6px; }
.eod-scroll::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 3px; }
.eod-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
.eod-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
@keyframes eod-toast-in { from { opacity: 0; transform: translateX(24px); } to { opacity: 1; transform: translateX(0); } }
.eod-toast { animation: eod-toast-in 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
`;

// Shared styles — modernized palette
const S = {
  card: { background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: 16, border: '1px solid rgba(0,0,0,0.04)' },
  sectionHeader: { background: 'linear-gradient(135deg, #4f46e5, #6366f1)', color: '#fff', padding: '11px 18px', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 },
  sectionHeaderGreen: { background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', padding: '11px 18px', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 },
  sectionHeaderBlue: { background: 'linear-gradient(135deg, #2563eb, #3b82f6)', color: '#fff', padding: '11px 18px', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 },
  sectionHeaderPurple: { background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)', color: '#fff', padding: '11px 18px', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 },
  sectionHeaderTeal: { background: 'linear-gradient(135deg, #0d9488, #14b8a6)', color: '#fff', padding: '11px 18px', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f8fafc', padding: '9px 12px', textAlign: 'center', fontWeight: 700, borderBottom: '2px solid #e2e8f0', fontSize: 11, textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.3 },
  thLeft: { background: '#f8fafc', padding: '9px 12px', textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e2e8f0', fontSize: 11, textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.3 },
  td: { padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #f1f5f9', color: '#334155' },
  tdLeft: { padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #f1f5f9', fontWeight: 600, color: '#1e293b' },
  tdBold: { padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #f1f5f9', fontWeight: 700, color: '#0f172a' },
  totalRow: { background: '#f8fafc', fontWeight: 700, borderTop: '2px solid #e2e8f0' },
  contriRow: { fontSize: 11, color: '#94a3b8', fontStyle: 'italic' },
  topBar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  title: { fontSize: 20, fontWeight: 800, color: '#4f46e5' },
  select: { padding: '7px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#334155' },
  input: { padding: '7px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, color: '#334155' },
  btn: { padding: '8px 20px', borderRadius: 10, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 13 },
  btnPrimary: { background: '#4f46e5', color: '#fff' },
  btnGreen: { background: '#059669', color: '#fff' },
  loading: { textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 16 },
};

// ============================================================================
// TOAST — lightweight top-right notification (no external deps)
// toast shape: { id, type: 'success'|'error'|'info', title, lines: [] }
// ============================================================================
function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return undefined;
    const ms = toast.type === 'error' ? 9000 : 6000;
    const t = setTimeout(onClose, ms);
    return () => clearTimeout(t);
  }, [toast, onClose]);

  if (!toast) return null;
  const accent = toast.type === 'error' ? '#dc2626' : toast.type === 'info' ? '#0ea5e9' : '#059669';
  const icon = toast.type === 'error' ? '⚠️' : toast.type === 'info' ? '🔄' : '✅';
  return (
    <div style={{ position: 'fixed', top: 18, right: 18, zIndex: 9999, maxWidth: 380 }}>
      <div className="eod-toast" style={{ background: '#fff', borderLeft: `4px solid ${accent}`, boxShadow: '0 10px 30px rgba(0,0,0,0.18)', borderRadius: 12, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 18, lineHeight: '20px' }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b', marginBottom: (toast.lines && toast.lines.length) ? 4 : 0 }}>{toast.title}</div>
          {(toast.lines || []).map((l, i) => (
            <div key={i} style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }}>{l}</div>
          ))}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8', lineHeight: '16px', padding: 0 }} title="Dismiss">×</button>
      </div>
    </div>
  );
}

// ============================================================================
// POS STAFF EOD REPORT (Image 1 - Single store FTD + MTD)
// ============================================================================
function StaffEODReport() {
  const { user } = useAuthStore();
  const canSync = (user?.roles || []).some(r => r === 'manager' || r === 'super_admin'); // Sync Shopify is manager-only
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [locFilter, setLocFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncFrom, setSyncFrom] = useState(new Date().toISOString().split('T')[0]);
  const [syncTo, setSyncTo] = useState(new Date().toISOString().split('T')[0]);
  const [toast, setToast] = useState(null);
  const clearToast = useCallback(() => setToast(null), []);
  const [showKPIForm, setShowKPIForm] = useState(false);
  const [kpiForm, setKpiForm] = useState({});
  const [kpiLocId, setKpiLocId] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('ftd');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/eod-report/staff-report', { params: { date, locFilter } });
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch staff EOD report:', e);
    }
    setLoading(false);
  }, [date, locFilter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Pull Shopify orders for the selected date range into the local DB (create missing, fill line
  // items, refresh existing), then toast the result and reload the report. Read-only vs Shopify.
  // The POST runs server-side to completion even if this page is reloaded (Node keeps the request
  // handler alive on client disconnect); a reload only drops the completion toast, not the sync.
  const syncShopify = () => {
    if (syncing) return;
    // Date range comes from the two Sync date inputs (start → end); capped at 10 days below.
    const dateFrom = syncFrom || date;
    const dateTo = syncTo || syncFrom || date;
    const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);
    if (!isDate(dateFrom) || !isDate(dateTo)) {
      setToast({ id: Date.now(), type: 'error', title: 'Invalid date(s)', lines: ['Use YYYY-MM-DD,YYYY-MM-DD (e.g. 2026-06-01,2026-06-10).'] });
      return;
    }
    if (dateTo < dateFrom) {
      setToast({ id: Date.now(), type: 'error', title: 'Invalid range', lines: ['"To" date must be on or after "From" date.'] });
      return;
    }
    const dayCount = Math.round((new Date(`${dateTo}T00:00:00`) - new Date(`${dateFrom}T00:00:00`)) / 86400000) + 1;
    if (dayCount > 10) {
      setToast({ id: Date.now(), type: 'error', title: 'Range too large', lines: [`${dayCount} days selected. Please choose 10 days or fewer.`] });
      return;
    }
    const rangeLabel = dateTo !== dateFrom ? `${dateFrom} → ${dateTo}` : dateFrom;
    setSyncing(true);
    setToast({ id: Date.now(), type: 'info', title: `Syncing Shopify — ${rangeLabel}`, lines: [`Fetching ${dayCount} day(s) of orders (read-only)…`] });
    api.post('/eod-report/sync-shopify', { dateFrom, dateTo }, { timeout: 300000 })
      .then(async (res) => {
        const s = res.data?.summary || {};
        setToast({
          id: Date.now(),
          type: (s.failed || 0) > 0 ? 'error' : 'success',
          title: `Shopify sync complete — ${rangeLabel}`,
          lines: [
            `Created: ${s.backfilled || 0}   •   Updated: ${s.updated || 0}`,
            `Line items filled: ${s.filledLi || 0}   •   Failed: ${s.failed || 0}`,
            `Fetched ${s.fetched || 0} Shopify orders`,
          ],
        });
        await fetchReport();
      })
      .catch((e) => {
        setToast({ id: Date.now(), type: 'error', title: 'Sync failed', lines: [e.response?.data?.error || e.message] });
      })
      .finally(() => setSyncing(false));
  };

  const loadKPIForm = useCallback(async (locId) => {
    if (!locId) return;
    try {
      const res = await api.get('/eod-report/daily-kpis', { params: { date, locationId: locId } });
      setKpiForm(res.data.kpis || {});
    } catch { setKpiForm({}); }
    setKpiLocId(locId);
    setShowKPIForm(true);
  }, [date]);

  const saveKPIs = async () => {
    setSaving(true);
    try {
      const locName = data?.locations?.find(l => String(l.locationId) === String(kpiLocId))?.locationName || '';
      await api.post('/eod-report/daily-kpis', { ...kpiForm, date, locationId: kpiLocId, locationName: locName });
      setShowKPIForm(false);
      fetchReport();
    } catch (e) {
      alert('Failed to save KPIs');
    }
    setSaving(false);
  };

  // Excel export — formatted with colors, matching on-screen layout with per-location columns
  const exportToExcel = async () => {
    if (!data || !data.locations) return;
    const realLocs = data.locations;
    const c = data.combined || {};
    // Store EOD export: collapse a multi-location login (store + Main Warehouse) into ONE combined
    // column (total in-store + warehouse), matching the on-screen view. (Aug 2026)
    const nonWhLocs = realLocs.filter(l => l.locationType !== 'warehouse');
    const locs = realLocs.length > 1
      ? [{ ...c, locationId: nonWhLocs[0]?.locationId || 'combined', locationName: nonWhLocs.length === 1 ? nonWhLocs[0].locationName : 'Combined', locationType: 'store' }]
      : realLocs;
    const locName = locs.length === 1 ? locs[0].locationName : 'Combined';
    const pctQ = (v, b) => b ? parseFloat(((v / b) * 100).toFixed(2)) + '%' : '0.00%';
    const locVal = (loc, section, period, field) => loc?.[section]?.[period]?.[field] ?? 0;
    const fmtV = (v, fmt) => fmt === 'inr' ? formatINR(v) : fmt === 'pct' ? formatPct(v) : v;

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('EOD Report');

    // FTD & MTD are shown SIDE BY SIDE. Simple sections: 2 value cols per loc (FTD, MTD).
    // Category sections: 4 cols per loc (FTD qty+value, MTD qty+value). (Aug 2026)
    const PERIODS = [['ftd', 'FTD'], ['mtd', 'MTD']];
    const multiLoc = locs.length > 1;
    const pHead = (loc, pLabel) => (multiLoc ? `${loc.locationName} ${pLabel}` : pLabel);
    const simpleCols = 1 + locs.length * 2;
    const catCols = 1 + locs.length * 4;
    const maxCols = Math.max(simpleCols, catCols);

    // Column widths
    const colWidths = [{ width: 42 }];
    for (let i = 1; i < maxCols; i++) colWidths.push({ width: 16 });
    ws.columns = colWidths;

    // Style presets
    const borderThin = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
    const fillOrange = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF57C00' } };
    const fillLightOrange = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3E0' } };
    const fillGreen = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };
    const fillLightGreen = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
    const fillBlue = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
    const fillPurple = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1FA2' } };
    const fillTeal = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00897B' } };
    const fillGrey = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
    const fillYellow = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8E1' } };
    const fontWhiteBold = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    const fontBold = { bold: true, size: 11 };
    const fontSmallGrey = { italic: true, color: { argb: 'FF888888' }, size: 10 };
    const alignCenter = { horizontal: 'center', vertical: 'middle' };

    const styleCells = (row, fill, font, cols) => {
      const n = cols || maxCols;
      for (let col = 1; col <= n; col++) {
        const cell = row.getCell(col);
        cell.border = borderThin;
        cell.alignment = col === 1 ? { vertical: 'middle' } : alignCenter;
        if (fill) cell.fill = fill;
        if (font) cell.font = font;
      }
    };

    // ===== Simple section helpers (FTD + MTD columns per location) =====
    // Header: [Section Title] [FTD] [MTD]  (or "<loc> FTD" / "<loc> MTD" when multi-location)
    const addSimpleSectionHeader = (text, fill) => {
      const vals = [text];
      locs.forEach(l => PERIODS.forEach(([, pl]) => vals.push(pHead(l, pl))));
      const row = ws.addRow(vals);
      styleCells(row, fill, fontWhiteBold, simpleCols);
      return row;
    };

    const addSimpleDataRow = (label, section, field, fmt, opts = {}) => {
      const vals = [label];
      locs.forEach(l => PERIODS.forEach(([p]) => vals.push(fmtV(locVal(l, section, p, field), fmt))));
      const row = ws.addRow(vals);
      styleCells(row, opts.fill, opts.bold ? fontBold : undefined, simpleCols);
      return row;
    };

    // ===== Category section helpers (per location: FTD qty+value, MTD qty+value) =====
    const addCatSectionHeader = (text, fill) => {
      const vals = [text];
      locs.forEach(l => PERIODS.forEach(([, pl]) => { vals.push(pHead(l, pl)); vals.push(''); }));
      const row = ws.addRow(vals);
      styleCells(row, fill, fontWhiteBold, catCols);
      // Merge each period's (qty, value) pair under one period label.
      let col = 2;
      locs.forEach(() => PERIODS.forEach(() => { ws.mergeCells(row.number, col, row.number, col + 1); col += 2; }));
      return row;
    };

    const addCatSubHeader = () => {
      const vals = [''];
      locs.forEach(() => PERIODS.forEach(() => { vals.push('QTY Sold'); vals.push('VALUE'); }));
      const row = ws.addRow(vals);
      styleCells(row, fillYellow, fontBold, catCols);
      return row;
    };

    const addCatDataRow = (label, cat, opts = {}) => {
      const getCat = (src, p) => src?.productCategories?.[p]?.[cat] || { qty: 0, value: 0 };
      const vals = [label];
      locs.forEach(l => PERIODS.forEach(([p]) => { const lc = getCat(l, p); vals.push(lc.qty); vals.push(formatINR(lc.value)); }));
      const row = ws.addRow(vals);
      styleCells(row, opts.fill, opts.bold ? fontBold : (opts.fontStyle || undefined), catCols);
      return row;
    };

    const addCatContriRow = (cat) => {
      const getBase = (src, p) => src?.productCategories?.[p + 'Total'] || { qty: 0, value: 0 };
      const getCat = (src, p) => src?.productCategories?.[p]?.[cat] || { qty: 0, value: 0 };
      const vals = ['Contri% of Total'];
      locs.forEach(l => PERIODS.forEach(([p]) => { const lc = getCat(l, p); const lb = getBase(l, p); vals.push(pctQ(lc.qty, lb.qty)); vals.push(pctQ(lc.value, lb.value)); }));
      const row = ws.addRow(vals);
      styleCells(row, fillGrey, fontSmallGrey, catCols);
      return row;
    };

    const addCatTotalRow = () => {
      const getTotal = (src, p) => src?.productCategories?.[p + 'Total'] || { qty: 0, value: 0 };
      // TOTAL row only (DISCOUNT / NET TOTAL rows removed per product decision, Aug 2026)
      const vals = ['TOTAL PRODUCTS'];
      locs.forEach(l => PERIODS.forEach(([p]) => { const lt = getTotal(l, p); vals.push(lt.qty); vals.push(formatINR(lt.value)); }));
      const row = ws.addRow(vals);
      styleCells(row, fillYellow, fontBold, catCols);
      return row;
    };

    // ===== HEADER =====
    const titleRow = ws.addRow(['', 'Store', 'Date']);
    titleRow.eachCell(cell => { cell.font = fontBold; cell.border = borderThin; });
    const infoRow = ws.addRow(['', locName, data.reportDate]);
    infoRow.eachCell(cell => { cell.border = borderThin; cell.font = fontBold; });
    ws.addRow([]);

    const mainTitle = ws.addRow(['OVERALL EOD SALES REPORT']);
    ws.mergeCells(mainTitle.number, 1, mainTitle.number, maxCols);
    mainTitle.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFE65100' } };
    mainTitle.getCell(1).alignment = alignCenter;
    ws.addRow([]);

    // Each section below shows FTD and MTD side by side (columns), not stacked.
    // ===== SALES REPORT =====
    addSimpleSectionHeader('SALES REPORT', fillOrange);
    [['SALES TARGET','salesTarget','inr'],['SALES ACH','salesAch','inr'],['SALES ACH %','salesAchPct','pct'],
     ['TOTAL WALKIN (New+Repeat+Other)','totalWalkins',''],['INVOICE ACH','invoiceAch',''],
     ['CASH COLLECTED','cashCollected','inr'],['ONLINE PAYMENT','onlinePayment','inr'],['SNAPMINT PAYMENT','snapmintPayment','inr'],['CARD PAYMENT','cardPayment','inr'],['UPI PAYMENT','upiPayment','inr']
    ].forEach(([label, field, fmt]) => addSimpleDataRow(label, 'salesReport', field, fmt, { fill: fillLightOrange }));
    ws.addRow([]);

    // ===== OPERATIONAL KPIs =====
    addSimpleSectionHeader("OPERATIONAL KPI'S", fillGreen);
    [['CONVERSION %','conversionPct','pct'],['ATV/ABV','atvAbv','inr'],['ASP','asp','inr'],
     ['UPT/ABS','uptAbs',''],['FOUNDERS MATTRESS SOLD QTY','foundersMattressSold',''],['MATTRESS TO PILLOW RATIO','mattressToPillowRatio','']
    ].forEach(([label, field, fmt]) => addSimpleDataRow(label, 'operationalKPIs', field, fmt, { fill: fillLightGreen }));
    ws.addRow([]);

    // ===== PRODUCT CATEGORY SOLD =====
    addCatSectionHeader('PRODUCT CATEGORY SOLD', fillOrange);
    addCatSubHeader();
    PRODUCT_CATEGORIES.forEach(cat => {
      addCatDataRow(cat, cat, { bold: true });
      addCatContriRow(cat);
    });
    addCatTotalRow();
    ws.addRow([]);

    // ===== OMNI KPIs =====
    addSimpleSectionHeader("OMNI KPI'S", fillBlue);
    [['NEW WALKIN','newWalkins'],['REPEAT WALKIN','repeatWalkins'],['OTHER WALKIN','otherWalkins'],
     ['NON BUYER QUOTATION SHARED','nonBuyerQuotation'],['NON BUYER CALL BACK','nonBuyerCallback'],['NON BUYER UNINTERESTED','nonBuyerUninterested']
    ].forEach(([label, field]) => addSimpleDataRow(label, 'omniKPIs', field, ''));
    ws.addRow([]);

    // ===== FUTURE BUSINESS =====
    addSimpleSectionHeader('FUTURE BUSINESS', fillPurple);
    addSimpleDataRow('QUOTATION SHARED QTY', 'futureBusiness', 'quotationSharedQty', '');
    addSimpleDataRow('QUOTATION VALUE', 'futureBusiness', 'quotationValue', '');
    ws.addRow([]);

    // ===== REVIEW UPDATE =====
    addSimpleSectionHeader('REVIEW UPDATE', fillTeal);
    addSimpleDataRow('GOOGLE REVIEWS TAKEN', 'reviewUpdate', 'googleReviewsTaken', '');
    addSimpleDataRow('GOOGLE REVIEWS to Walkin %', 'reviewUpdate', 'googleReviewsToWalkinPct', 'pct');
    ws.addRow([]);

    // ===== STAFF PERFORMANCE =====
    if (data.staffBreakdown?.length > 0) {
      const staffHeader = ws.addRow(['STAFF PERFORMANCE', 'FTD Orders', 'FTD Sales', 'FTD Units', 'MTD Orders', 'MTD Sales', 'MTD Units']);
      staffHeader.eachCell((cell, col) => {
        cell.fill = fillPurple; cell.font = fontWhiteBold; cell.border = borderThin;
        cell.alignment = col === 1 ? { vertical: 'middle' } : alignCenter;
      });
      data.staffBreakdown.forEach((st, i) => {
        const row = ws.addRow([st.staffName, st.ftdOrders || 0, formatINR(st.ftdSales), st.ftdUnits, st.mtdOrders || 0, formatINR(st.mtdSales), st.mtdUnits]);
        row.eachCell((cell, col) => {
          cell.border = borderThin;
          cell.alignment = col === 1 ? { vertical: 'middle' } : alignCenter;
          if (i === 0) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E5F5' } };
        });
      });
      const totS = data.staffBreakdown;
      const totalRow = ws.addRow(['TOTAL',
        totS.reduce((s, st) => s + (st.ftdOrders || 0), 0),
        formatINR(totS.reduce((s, st) => s + st.ftdSales, 0)), totS.reduce((s, st) => s + st.ftdUnits, 0),
        totS.reduce((s, st) => s + (st.mtdOrders || 0), 0),
        formatINR(totS.reduce((s, st) => s + st.mtdSales, 0)), totS.reduce((s, st) => s + st.mtdUnits, 0)]);
      totalRow.eachCell((cell, col) => {
        cell.fill = fillYellow; cell.font = fontBold; cell.border = borderThin;
        cell.alignment = col === 1 ? { vertical: 'middle' } : alignCenter;
      });
    }

    // Generate and download
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `EOD_Report_${data.reportDate}.xlsx`);
  };

  if (loading) return <div className="eod-root" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="eod-loading-pulse" style={{ fontSize: 18, color: '#94a3b8', fontWeight: 600 }}>Loading EOD Report...</div></div>;
  if (!data || !data.locations) return <div style={S.loading}>No report data. Click refresh to load.</div>;

  const realLocs = data.locations || [];
  const c = data.combined || {};
  // Store EOD: when the login spans multiple locations (its store + Main Warehouse), show ONE
  // combined column (total in-store + warehouse) instead of separate In-Store / Warehouse columns
  // plus a Combined column. The real per-location list (`realLocs`) is kept for the "Enter Daily
  // KPIs" dropdown, which needs the actual store locationId. (Aug 2026)
  const nonWhLocs = realLocs.filter(l => l.locationType !== 'warehouse');
  const combinedLoc = realLocs.length > 1
    ? { ...c, locationId: nonWhLocs[0]?.locationId || 'combined', locationName: nonWhLocs.length === 1 ? nonWhLocs[0].locationName : 'Combined', locationType: 'store' }
    : null;
  const locs = combinedLoc ? [combinedLoc] : realLocs;
  const staffList = data.staffBreakdown || [];
  const locNames = locs.map(l => l.locationName);
  const hasMultiple = locs.length > 1;

  // Helper: get value from a nested path per location
  const locVal = (loc, section, period, field) => {
    const s = loc?.[section]?.[period];
    return s ? s[field] : 0;
  };

  // Multi-col row helper for staff report (label | combined | loc1 | loc2 ...)
  const MRow = ({ label, section, period, field, format = 'num', bold }) => {
    const fmt = (v) => {
      if (format === 'inr') return formatINR(v);
      if (format === 'pct') return formatPct(v);
      return v ?? 0;
    };
    const combinedVal = c?.[section]?.[period]?.[field];
    return (
      <tr style={bold ? S.totalRow : {}}>
        <td style={{ ...S.tdLeft, fontWeight: bold ? 700 : 600, position: 'sticky', left: 0, background: bold ? '#f8fafc' : '#fff', zIndex: 1 }}>{label}</td>
        {hasMultiple && <td style={S.tdBold}>{fmt(combinedVal)}</td>}
        {locs.map(l => <td key={l.locationId} style={S.td}>{fmt(locVal(l, section, period, field))}</td>)}
      </tr>
    );
  };

  // Category color map for visual badges
  const catColors = {
    'Mattresses': '#4f46e5', 'Founders': '#7c3aed', 'Bed': '#2563eb', 'Recliner': '#059669',
    'Mobility': '#0d9488', 'Dog Bed': '#ea580c', 'Pillow': '#8b5cf6', 'Comforter': '#0284c7',
    'Throw': '#65a30d', 'Mattress Topper': '#e11d48', 'Sheet & Cover': '#78716c',
    'Mattress Protector': '#475569', 'Mattress Cover': '#92400e', 'Weighted Blanket': '#d97706',
    'Pillow Cases': '#a855f7', 'Others': '#6b7280',
  };

  // Category row helper with contribution % and visual bars
  const CatRow = ({ cat, period }) => {
    const combinedCat = c?.productCategories?.[period]?.[cat] || { qty: 0, value: 0, orders: 0 };
    const combinedTotal = c?.productCategories?.[period + 'Total'] || { qty: 0, value: 0, orders: 0 };
    const base = combinedTotal;
    const pctQ = (v, b) => b ? ((v / b) * 100) : 0;
    const pctFmt = (v) => formatPct(v);
    const catColor = catColors[cat] || '#666';
    const valuePct = pctQ(combinedCat.value, base.value);
    const hasData = combinedCat.qty > 0 || locs.some(l => (l.productCategories?.[period]?.[cat]?.qty || 0) > 0);
    const cS = { fontSize: 10, color: '#94a3b8', paddingTop: 1, paddingBottom: 4, borderBottom: '1px solid #f1f5f9', textAlign: 'center' };

    return (
      <React.Fragment>
        <tr style={{ borderLeft: `3px solid ${catColor}`, background: hasData ? '#fff' : '#fafafa' }}>
          <td style={{ ...S.tdLeft, fontWeight: 700, padding: '6px 10px', position: 'sticky', left: 0, background: hasData ? '#fff' : '#fafafa', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: catColor, display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontSize: 12 }}>{cat}</span>
            </div>
          </td>
          {hasMultiple && <>
            <td style={{ ...S.td, fontSize: 12, fontWeight: 700, textAlign: 'center', color: hasData ? '#1e293b' : '#cbd5e1' }}>{combinedCat.qty}</td>
            <td style={{ ...S.td, fontSize: 12, fontWeight: 600, textAlign: 'center', color: hasData ? '#059669' : '#cbd5e1' }}>{formatINR(combinedCat.value)}</td>
            <td style={{ ...S.td, fontSize: 12, fontWeight: 600, textAlign: 'center', color: hasData ? '#2563eb' : '#cbd5e1' }}>{combinedCat.orders || 0}</td>
          </>}
          {locs.map(l => {
            const lc = l.productCategories?.[period]?.[cat] || { qty: 0, value: 0, orders: 0 };
            const ld = lc.qty > 0;
            return <React.Fragment key={l.locationId}>
              <td style={{ ...S.td, fontSize: 12, fontWeight: 700, textAlign: 'center', color: ld ? '#1e293b' : '#cbd5e1' }}>{lc.qty}</td>
              <td style={{ ...S.td, fontSize: 12, fontWeight: 600, textAlign: 'center', color: ld ? '#059669' : '#cbd5e1' }}>{formatINR(lc.value)}</td>
              <td style={{ ...S.td, fontSize: 12, fontWeight: 600, textAlign: 'center', color: ld ? '#2563eb' : '#cbd5e1' }}>{lc.orders || 0}</td>
            </React.Fragment>;
          })}
        </tr>
        {hasData && <tr style={{ borderLeft: `3px solid ${catColor}20` }}>
          <td style={{ paddingLeft: 28, paddingTop: 1, paddingBottom: 4, borderBottom: '1px solid #f0f0f0', fontSize: 10, color: '#aaa', position: 'sticky', left: 0, background: '#fafafa', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontStyle: 'italic' }}>Contri%</span>
              {hasMultiple && valuePct > 0 && (
                <div style={{ width: 50, height: 3, background: '#eee', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(valuePct, 100)}%`, height: '100%', background: catColor, borderRadius: 2 }} />
                </div>
              )}
            </div>
          </td>
          {hasMultiple && <><td style={cS}>{pctFmt(pctQ(combinedCat.qty, base.qty))}</td><td style={cS}>{pctFmt(valuePct)}</td><td style={cS}>{pctFmt(pctQ(combinedCat.orders, base.orders))}</td></>}
          {locs.map(l => {
            const lc = l.productCategories?.[period]?.[cat] || { qty: 0, value: 0, orders: 0 };
            const lb = l.productCategories?.[period + 'Total'] || { qty: 0, value: 0, orders: 0 };
            return <React.Fragment key={l.locationId}><td style={cS}>{pctFmt(pctQ(lc.qty, lb.qty))}</td><td style={cS}>{pctFmt(pctQ(lc.value, lb.value))}</td><td style={cS}>{pctFmt(pctQ(lc.orders, lb.orders))}</td></React.Fragment>;
          })}
        </tr>}
      </React.Fragment>
    );
  };

  // Stat card helper
  const StatCard = ({ icon, label, value, color, sub }) => (
    <div className="eod-stat-card" style={{ flex: '1 1 140px', background: '#fff', borderRadius: 14, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)', borderLeft: `4px solid ${color}`, minWidth: 140, border: '1px solid rgba(0,0,0,0.04)', borderLeftWidth: 4, borderLeftColor: color }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
        <span style={{ fontSize: 14 }}>{icon}</span> {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: -0.5 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontWeight: 500 }}>{sub}</div>}
    </div>
  );

  // Period tab helper
  const pd = activeTab; // 'ftd' or 'mtd'
  const pdLabel = pd === 'ftd' ? 'FTD' : 'MTD';

  // Quick summary values for stat cards
  const salesAch = c?.salesReport?.[pd]?.salesAch || 0;
  const salesTarget = c?.salesReport?.[pd]?.salesTarget || 0;
  const salesAchPct = c?.salesReport?.[pd]?.salesAchPct || 0;
  const totalWalkins = c?.salesReport?.[pd]?.totalWalkins || 0;
  const invoiceAch = c?.salesReport?.[pd]?.invoiceAch || 0;
  const convPct = c?.operationalKPIs?.[pd]?.conversionPct || 0;

  return (
    <div className="eod-root eod-animate">
      <style>{EOD_CSS}</style>
      <Toast toast={toast} onClose={clearToast} />
      {/* Header */}
      <div style={{ ...S.card, background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 50%, #c7d2fe 100%)', padding: 0, overflow: 'hidden' }}>
        {/* Top row: Location + Date */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderBottom: '1px solid rgba(79,70,229,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #4f46e5, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff' }}>📍</div>
            <div>
              <div style={{ fontSize: 10, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 600 }}>Locations</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#312e81' }}>{locNames.join(' + ')}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📅</div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 600 }}>Report Date</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>{data.reportDate}</div>
            </div>
          </div>
        </div>
        {/* Bottom row: Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 22px', flexWrap: 'wrap', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(8px)' }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="eod-input" style={S.input} />
          <select value={locFilter} onChange={e => setLocFilter(e.target.value)} className="eod-input" style={S.select}>
            <option value="store">Store Only</option>
            <option value="warehouse">Warehouse Only</option>
            <option value="all">Store + Warehouse</option>
          </select>
          <button onClick={fetchReport} className="eod-btn" style={{ ...S.btn, ...S.btnPrimary }}>Refresh</button>
          {canSync && <>
            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>Sync</span>
            <input type="date" value={syncFrom} onChange={e => { const v = e.target.value; setSyncFrom(v); if (syncTo && v > syncTo) setSyncTo(v); }} className="eod-input" style={S.input} title="Sync start date" />
            <span style={{ fontSize: 11, color: '#64748b' }}>→</span>
            <input type="date" value={syncTo} onChange={e => { const v = e.target.value; setSyncTo(v); if (syncFrom && v < syncFrom) setSyncFrom(v); }} className="eod-input" style={S.input} title="Sync end date (max 10 days)" />
            <button onClick={syncShopify} disabled={syncing} className="eod-btn" style={{ ...S.btn, background: '#0ea5e9', color: '#fff', opacity: syncing ? 0.7 : 1 }} title="Fetch the selected date range of orders from Shopify (max 10 days)">{syncing ? 'Syncing…' : '🔄 Sync Shopify'}</button>
          </>}
          <div style={{ flex: 1 }} />
          <select onChange={e => { if (e.target.value) loadKPIForm(e.target.value); }} defaultValue="" className="eod-input" style={S.select}>
            <option value="" disabled>Enter Daily KPIs for...</option>
            {realLocs.filter(l => l.locationType !== 'warehouse').map(l => <option key={l.locationId} value={l.locationId}>{l.locationName}</option>)}
          </select>
          <button onClick={exportToExcel} className="eod-btn" style={{ ...S.btn, ...S.btnGreen }}>Export Excel</button>
        </div>
      </div>

      {/* KPI Entry Form for Staff */}
      {showKPIForm && (
        <div style={{ ...S.card, padding: 20, border: '2px solid #6366f1' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#4f46e5' }}>
              Enter Daily KPIs — {realLocs.find(l => String(l.locationId) === String(kpiLocId))?.locationName || kpiLocId}
            </div>
          </div>
          <KPIEntryForm kpiForm={kpiForm} setKpiForm={setKpiForm} onSave={saveKPIs} onCancel={() => setShowKPIForm(false)} saving={saving} />
        </div>
      )}

      {/* Summary Stat Cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <StatCard icon="💰" label={`${pdLabel} Sales`} value={formatINR(salesAch)} color="#f57c00" sub={`Target: ${formatINR(salesTarget)}`} />
        <StatCard icon="📊" label={`${pdLabel} Ach%`} value={formatPct(salesAchPct)} color={salesAchPct >= 100 ? '#2e7d32' : salesAchPct >= 70 ? '#f57c00' : '#c62828'} />
        <StatCard icon="🧾" label={`${pdLabel} Invoices`} value={invoiceAch} color="#1565c0" />
        <StatCard icon="🚶" label={`${pdLabel} Walk-ins`} value={totalWalkins} color="#6a1b9a" />
        <StatCard icon="🎯" label={`${pdLabel} Conversion`} value={formatPct(convPct)} color="#00695c" />
      </div>

      {/* FTD / MTD Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16, background: '#f1f5f9', borderRadius: 12, padding: 3 }}>
        {['ftd', 'mtd'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className="eod-tab" style={{
            padding: '10px 28px', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', flex: 1,
            background: activeTab === tab ? (tab === 'ftd' ? 'linear-gradient(135deg, #2563eb, #3b82f6)' : 'linear-gradient(135deg, #4f46e5, #6366f1)') : 'transparent',
            color: activeTab === tab ? '#fff' : '#64748b',
            borderRadius: 10,
            transition: 'all 0.25s ease',
          }}>
            {tab === 'ftd' ? 'FTD — For The Day' : `MTD — ${data.mtdLiveDays} Live Days`}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 15, fontWeight: 800, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: 1 }}>
          Overall EOD Sales Report
        </div>
      </div>

      {/* === DYNAMIC SECTION (FTD or MTD based on tab) === */}

      {/* Sales Report */}
      <div style={S.card}>
        <div style={S.sectionHeader} className="eod-section-header">SALES REPORT — {pdLabel}</div>
        <div style={{ overflowX: 'auto' }} className="eod-scroll">
        <table style={{ ...S.table, minWidth: 200 + locs.length * 130 }} className="eod-table">
          <thead>
            <tr>
              <th style={{ ...S.thLeft, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1, minWidth: 180 }}></th>
              {hasMultiple && <th style={{ ...S.th, minWidth: 100 }}>Combined</th>}
              {locs.map(l => <th key={l.locationId} style={{ ...S.th, minWidth: 110, whiteSpace: 'nowrap' }}>{l.locationName}</th>)}
            </tr>
          </thead>
          <tbody>
            <MRow label="SALES TARGET" section="salesReport" period={pd} field="salesTarget" format="inr" />
            <MRow label="SALES ACH" section="salesReport" period={pd} field="salesAch" format="inr" bold />
            <MRow label="SALES ACH %" section="salesReport" period={pd} field="salesAchPct" format="pct" />
            <MRow label="TOTAL WALKIN (New+Repeat+Other)" section="salesReport" period={pd} field="totalWalkins" />
            <MRow label="INVOICE ACH" section="salesReport" period={pd} field="invoiceAch" />
            <MRow label="CASH COLLECTED" section="salesReport" period={pd} field="cashCollected" format="inr" />
            <MRow label="ONLINE PAYMENT" section="salesReport" period={pd} field="onlinePayment" format="inr" />
            <MRow label="SNAPMINT PAYMENT" section="salesReport" period={pd} field="snapmintPayment" format="inr" />
            <MRow label="CARD PAYMENT" section="salesReport" period={pd} field="cardPayment" format="inr" />
            <MRow label="UPI PAYMENT" section="salesReport" period={pd} field="upiPayment" format="inr" />
          </tbody>
        </table>
        </div>
      </div>

      {/* Operational KPIs */}
      <div style={S.card}>
        <div style={S.sectionHeaderGreen} className="eod-section-header">OPERATIONAL KPI'S — {pdLabel}</div>
        <div style={{ overflowX: 'auto' }} className="eod-scroll">
        <table style={{ ...S.table, minWidth: 200 + locs.length * 130 }} className="eod-table">
          <thead>
            <tr>
              <th style={{ ...S.thLeft, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1, minWidth: 180 }}></th>
              {hasMultiple && <th style={{ ...S.th, minWidth: 100 }}>Combined</th>}
              {locs.map(l => <th key={l.locationId} style={{ ...S.th, minWidth: 110, whiteSpace: 'nowrap' }}>{l.locationName}</th>)}
            </tr>
          </thead>
          <tbody>
            <MRow label="CONVERSION %" section="operationalKPIs" period={pd} field="conversionPct" format="pct" />
            <MRow label="ATV/ABV" section="operationalKPIs" period={pd} field="atvAbv" format="inr" />
            <MRow label="ASP" section="operationalKPIs" period={pd} field="asp" format="inr" />
            <MRow label="UPT/ABS" section="operationalKPIs" period={pd} field="uptAbs" />
            <MRow label="FOUNDERS MATTRESS SOLD QTY" section="operationalKPIs" period={pd} field="foundersMattressSold" />
            <MRow label="MATTRESS TO PILLOW RATIO" section="operationalKPIs" period={pd} field="mattressToPillowRatio" />
          </tbody>
        </table>
        </div>
      </div>

      {/* Product Categories */}
      <div style={S.card}>
        <div style={{ ...S.sectionHeader, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className="eod-section-header">
          <span>PRODUCT CATEGORY SOLD — {pdLabel}</span>
          <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>{PRODUCT_CATEGORIES.length} Categories</span>
        </div>
        <div style={{ overflowX: 'auto' }} className="eod-scroll">
          <table style={{ ...S.table, borderSpacing: 0, borderCollapse: 'collapse', minWidth: 200 + locs.length * 280 }}>
            <thead>
              {/* Group header row — location names spanning Qty+Value+Order */}
              <tr>
                <th rowSpan={2} style={{ ...S.thLeft, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 2, minWidth: 150, borderBottom: '2px solid #e2e8f0', verticalAlign: 'bottom', padding: '8px 12px' }}>Category</th>
                {hasMultiple && <th colSpan={3} style={{ ...S.th, background: '#eef2ff', color: '#4f46e5', borderBottom: '1px solid #c7d2fe', padding: '6px 8px', fontSize: 12, fontWeight: 800 }}>COMBINED</th>}
                {locs.map(l => <th key={l.locationId} colSpan={3} style={{ ...S.th, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '6px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{l.locationName.replace(/ *\(Combined\)/i, '')}</th>)}
              </tr>
              {/* Sub-header row — Qty / Value / Order under each group */}
              <tr>
                {hasMultiple && <><th style={{ ...S.th, background: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderBottom: '2px solid #4f46e5' }}>Qty</th><th style={{ ...S.th, background: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderBottom: '2px solid #4f46e5' }}>Value (₹)</th><th style={{ ...S.th, background: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderBottom: '2px solid #4f46e5' }}>Orders</th></>}
                {locs.map(l => <React.Fragment key={l.locationId}><th style={{ ...S.th, fontSize: 10, fontWeight: 700, color: '#64748b', padding: '4px 8px', borderBottom: '2px solid #e2e8f0' }}>Qty</th><th style={{ ...S.th, fontSize: 10, fontWeight: 700, color: '#64748b', padding: '4px 8px', borderBottom: '2px solid #e2e8f0' }}>Value (₹)</th><th style={{ ...S.th, fontSize: 10, fontWeight: 700, color: '#2563eb', padding: '4px 8px', borderBottom: '2px solid #e2e8f0' }}>Orders</th></React.Fragment>)}
              </tr>
            </thead>
            <tbody>
              {PRODUCT_CATEGORIES.map(cat => <CatRow key={cat} cat={cat} period={pd} />)}
              {/* TOTAL row */}
              <tr style={{ background: 'linear-gradient(90deg, #eef2ff, #e0e7ff)', borderTop: '2px solid #4f46e5' }}>
                <td style={{ ...S.tdLeft, fontWeight: 800, padding: '10px 12px', fontSize: 13, position: 'sticky', left: 0, background: '#eef2ff', zIndex: 1 }}>📦 TOTAL</td>
                {hasMultiple && <><td style={{ ...S.tdBold, fontSize: 13 }}>{c.productCategories?.[pd + 'Total']?.qty || 0}</td><td style={{ ...S.tdBold, fontSize: 13, color: '#059669' }}>{formatINR(c.productCategories?.[pd + 'Total']?.value)}</td><td style={{ ...S.tdBold, fontSize: 13, color: '#2563eb' }}>{c.productCategories?.[pd + 'Total']?.orders || 0}</td></>}
                {locs.map(l => <React.Fragment key={l.locationId}><td style={{ ...S.tdBold, fontSize: 13 }}>{l.productCategories?.[pd + 'Total']?.qty || 0}</td><td style={{ ...S.tdBold, fontSize: 13, color: '#059669' }}>{formatINR(l.productCategories?.[pd + 'Total']?.value)}</td><td style={{ ...S.tdBold, fontSize: 13, color: '#2563eb' }}>{l.productCategories?.[pd + 'Total']?.orders || 0}</td></React.Fragment>)}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Omni KPIs */}
      <div style={S.card}>
        <div style={S.sectionHeaderBlue} className="eod-section-header">OMNI KPI'S — {pdLabel}</div>
        <div style={{ overflowX: 'auto' }} className="eod-scroll">
          <table style={{ ...S.table, minWidth: 200 + locs.length * 130 }} className="eod-table">
            <thead>
              <tr>
                <th style={{ ...S.thLeft, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1, minWidth: 180 }}></th>
                {hasMultiple && <th style={{ ...S.th, minWidth: 100 }}>Combined</th>}
                {locs.map(l => <th key={l.locationId} style={{ ...S.th, minWidth: 110, whiteSpace: 'nowrap' }}>{l.locationName}</th>)}
              </tr>
            </thead>
            <tbody>
              <MRow label="NEW WALKIN" section="omniKPIs" period={pd} field="newWalkins" />
              <MRow label="REPEAT WALKIN" section="omniKPIs" period={pd} field="repeatWalkins" />
              <MRow label="OTHER WALKIN" section="omniKPIs" period={pd} field="otherWalkins" />
              <MRow label="NON BUYER QUOTATION" section="omniKPIs" period={pd} field="nonBuyerQuotation" />
              <MRow label="NON BUYER CALL BACK" section="omniKPIs" period={pd} field="nonBuyerCallback" />
              <MRow label="NON BUYER UNINTERESTED" section="omniKPIs" period={pd} field="nonBuyerUninterested" />
            </tbody>
          </table>
        </div>
      </div>

      {/* Future Business + Reviews side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: locs.length > 5 ? '1fr' : '1fr 1fr', gap: 16 }}>
        <div style={S.card}>
          <div style={S.sectionHeaderPurple} className="eod-section-header">FUTURE BUSINESS — {pdLabel}</div>
          <div style={{ overflowX: 'auto' }} className="eod-scroll">
            <table style={{ ...S.table, minWidth: 200 + locs.length * 130 }} className="eod-table">
              <thead><tr><th style={{ ...S.thLeft, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1, minWidth: 180 }}></th>{hasMultiple && <th style={{ ...S.th, minWidth: 100 }}>Combined</th>}{locs.map(l => <th key={l.locationId} style={{ ...S.th, minWidth: 110, whiteSpace: 'nowrap' }}>{l.locationName}</th>)}</tr></thead>
              <tbody>
                <MRow label="QUOTATION SHARED QTY" section="futureBusiness" period={pd} field="quotationSharedQty" />
                <MRow label="QUOTATION VALUE" section="futureBusiness" period={pd} field="quotationValue" format="inr" />
              </tbody>
            </table>
          </div>
        </div>
        <div style={S.card}>
          <div style={S.sectionHeaderTeal} className="eod-section-header">REVIEW UPDATE — {pdLabel}</div>
          <div style={{ overflowX: 'auto' }} className="eod-scroll">
            <table style={{ ...S.table, minWidth: 200 + locs.length * 130 }} className="eod-table">
              <thead><tr><th style={{ ...S.thLeft, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1, minWidth: 180 }}></th>{hasMultiple && <th style={{ ...S.th, minWidth: 100 }}>Combined</th>}{locs.map(l => <th key={l.locationId} style={{ ...S.th, minWidth: 110, whiteSpace: 'nowrap' }}>{l.locationName}</th>)}</tr></thead>
              <tbody>
                <MRow label="GOOGLE REVIEWS TAKEN" section="reviewUpdate" period={pd} field="googleReviewsTaken" />
                <MRow label="REVIEWS to Walkin %" section="reviewUpdate" period={pd} field="googleReviewsToWalkinPct" format="pct" />
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* === STAFF PERFORMANCE === */}
      {staffList.length > 0 && (
        <div style={S.card}>
          <div style={{ ...S.sectionHeader, background: 'linear-gradient(135deg, #4338ca, #6366f1)' }} className="eod-section-header">STAFF PERFORMANCE</div>
          <div style={{ padding: 16 }}>
            {/* Staff summary cards */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {staffList.map((st, i) => {
                const isTop = i === 0;
                return (
                  <div key={i} className="eod-stat-card" style={{
                    flex: '1 1 200px', minWidth: 200, borderRadius: 12, padding: '12px 16px',
                    background: isTop ? 'linear-gradient(135deg, #eef2ff, #e0e7ff)' : '#fafafa',
                    border: isTop ? '2px solid #6366f1' : '1px solid #e2e8f0',
                    position: 'relative',
                  }}>
                    {isTop && <div style={{ position: 'absolute', top: -8, right: 8, background: '#4f46e5', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10 }}>TOP</div>}
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#312e81', marginBottom: 6 }}>{st.staffName}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 12 }}>
                      <div><span style={{ color: '#64748b' }}>FTD Orders:</span> <b>{st.ftdOrders || 0}</b></div>
                      <div><span style={{ color: '#64748b' }}>MTD Orders:</span> <b>{st.mtdOrders || 0}</b></div>
                      <div><span style={{ color: '#64748b' }}>FTD Sales:</span> <b style={{ color: '#059669' }}>{formatINR(st.ftdSales)}</b></div>
                      <div><span style={{ color: '#64748b' }}>MTD Sales:</span> <b style={{ color: '#2563eb' }}>{formatINR(st.mtdSales)}</b></div>
                      <div><span style={{ color: '#64748b' }}>FTD Units:</span> <b>{st.ftdUnits}</b></div>
                      <div><span style={{ color: '#64748b' }}>MTD Units:</span> <b>{st.mtdUnits}</b></div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Staff detail table */}
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.thLeft}>Staff Name</th>
                  <th style={S.th}>FTD Orders</th>
                  <th style={S.th}>FTD Sales</th>
                  <th style={S.th}>FTD Units</th>
                  <th style={S.th}>MTD Orders</th>
                  <th style={S.th}>MTD Sales</th>
                  <th style={S.th}>MTD Units</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((st, i) => (
                  <tr key={i} style={i % 2 === 0 ? { background: '#fafafa' } : {}}>
                    <td style={{ ...S.tdLeft, fontWeight: 700 }}>{st.staffName}</td>
                    <td style={S.td}>{st.ftdOrders || 0}</td>
                    <td style={S.tdBold}>{formatINR(st.ftdSales)}</td>
                    <td style={S.td}>{st.ftdUnits}</td>
                    <td style={S.td}>{st.mtdOrders || 0}</td>
                    <td style={S.tdBold}>{formatINR(st.mtdSales)}</td>
                    <td style={S.td}>{st.mtdUnits}</td>
                  </tr>
                ))}
                <tr style={S.totalRow}>
                  <td style={{ ...S.tdLeft, fontWeight: 800 }}>TOTAL</td>
                  <td style={S.tdBold}>{staffList.reduce((s, st) => s + (st.ftdOrders || 0), 0)}</td>
                  <td style={S.tdBold}>{formatINR(staffList.reduce((s, st) => s + st.ftdSales, 0))}</td>
                  <td style={S.tdBold}>{staffList.reduce((s, st) => s + st.ftdUnits, 0)}</td>
                  <td style={S.tdBold}>{staffList.reduce((s, st) => s + (st.mtdOrders || 0), 0)}</td>
                  <td style={S.tdBold}>{formatINR(staffList.reduce((s, st) => s + st.mtdSales, 0))}</td>
                  <td style={S.tdBold}>{staffList.reduce((s, st) => s + st.mtdUnits, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MANAGER EOD REPORT (Image 2 - Multi-store FTD + MTD)
// ============================================================================
function ManagerEODReport() {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [locFilter, setLocFilter] = useState('combined');
  const [storeFilter, setStoreFilter] = useState('all'); // 'all' or a specific store's locationId
  const [storeOptions, setStoreOptions] = useState([]);   // stable list for the store dropdown
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncFrom, setSyncFrom] = useState(new Date().toISOString().split('T')[0]);
  const [syncTo, setSyncTo] = useState(new Date().toISOString().split('T')[0]);
  const [toast, setToast] = useState(null);
  const clearToast = useCallback(() => setToast(null), []);
  const [allLocations, setAllLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('ftd');
  // Target entry state
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [targetLocId, setTargetLocId] = useState('');
  const [targetMonth, setTargetMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dailyStatus, setDailyStatus] = useState(null); // {count,total} of uploaded daily targets for selected store/month
  const [existingTargets, setExistingTargets] = useState([]);
  const [targetDetail, setTargetDetail] = useState(null); // {locationId, locationName} whose day-wise breakdown modal is open
  const [targetDetailRows, setTargetDetailRows] = useState([]); // [{target_date, daily_target}]
  const [targetDetailLoading, setTargetDetailLoading] = useState(false);
  const [dailyBusy, setDailyBusy] = useState(false); // daily-target CSV upload in progress

  useEffect(() => {
    api.get('/locations').then(res => {
      if (res.data?.locations) setAllLocations(res.data.locations);
    }).catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/eod-report/manager-report', { params: { date, locFilter, storeId: storeFilter } });
      setData(res.data);
      // Keep the store dropdown populated from the full (unfiltered) store list so selecting one
      // store doesn't shrink the options. Skip warehouse-only view (those aren't stores).
      if (storeFilter === 'all' && locFilter !== 'warehouse' && Array.isArray(res.data?.stores)) {
        setStoreOptions(res.data.stores.map(s => ({
          id: s.locationId,
          name: (s.locationName || '').replace(/ *\(Combined\)/i, ''),
        })));
      }
    } catch (e) {
      console.error('Failed to fetch manager EOD report:', e);
    }
    setLoading(false);
  }, [date, locFilter, storeFilter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Pull Shopify orders for the selected day into the local DB in the BACKGROUND, then toast the
  // result and reload the region report.
  const syncShopify = () => {
    if (syncing) return;
    // Date range comes from the two Sync date inputs (start → end); capped at 10 days below.
    const dateFrom = syncFrom || date;
    const dateTo = syncTo || syncFrom || date;
    const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);
    if (!isDate(dateFrom) || !isDate(dateTo)) {
      setToast({ id: Date.now(), type: 'error', title: 'Invalid date(s)', lines: ['Use YYYY-MM-DD,YYYY-MM-DD (e.g. 2026-06-01,2026-06-10).'] });
      return;
    }
    if (dateTo < dateFrom) {
      setToast({ id: Date.now(), type: 'error', title: 'Invalid range', lines: ['"To" date must be on or after "From" date.'] });
      return;
    }
    const dayCount = Math.round((new Date(`${dateTo}T00:00:00`) - new Date(`${dateFrom}T00:00:00`)) / 86400000) + 1;
    if (dayCount > 10) {
      setToast({ id: Date.now(), type: 'error', title: 'Range too large', lines: [`${dayCount} days selected. Please choose 10 days or fewer.`] });
      return;
    }
    const rangeLabel = dateTo !== dateFrom ? `${dateFrom} → ${dateTo}` : dateFrom;
    setSyncing(true);
    setToast({ id: Date.now(), type: 'info', title: `Syncing Shopify — ${rangeLabel}`, lines: [`Fetching ${dayCount} day(s) of orders (read-only)…`] });
    api.post('/eod-report/sync-shopify', { dateFrom, dateTo }, { timeout: 300000 })
      .then(async (res) => {
        const s = res.data?.summary || {};
        setToast({
          id: Date.now(),
          type: (s.failed || 0) > 0 ? 'error' : 'success',
          title: `Shopify sync complete — ${rangeLabel}`,
          lines: [
            `Created: ${s.backfilled || 0}   •   Updated: ${s.updated || 0}`,
            `Line items filled: ${s.filledLi || 0}   •   Failed: ${s.failed || 0}`,
            `Fetched ${s.fetched || 0} Shopify orders`,
          ],
        });
        await fetchReport();
      })
      .catch((e) => {
        setToast({ id: Date.now(), type: 'error', title: 'Sync failed', lines: [e.response?.data?.error || e.message] });
      })
      .finally(() => setSyncing(false));
  };

  const loadTargets = useCallback(async (month) => {
    try {
      const res = await api.get('/eod-report/targets', { params: { month: month + '-01' } });
      setExistingTargets(res.data.targets || []);
    } catch { setExistingTargets([]); }
  }, []);

  useEffect(() => { if (showTargetForm) loadTargets(targetMonth); }, [showTargetForm, targetMonth, loadTargets]);

  // Open the day-wise breakdown modal for a store's uploaded daily sheet (the exact values uploaded).
  const openTargetDetail = async (t) => {
    setTargetDetail({ locationId: t.location_id, locationName: (t.location_name || t.location_id || '').replace(/ *\(Combined\)/i, '') });
    setTargetDetailLoading(true);
    setTargetDetailRows([]);
    try {
      const res = await api.get('/eod-report/targets/daily', { params: { locationId: t.location_id, month: targetMonth + '-01' } });
      setTargetDetailRows(res.data.rows || []);
    } catch { setTargetDetailRows([]); }
    setTargetDetailLoading(false);
  };

  // Load the currently-uploaded daily-target total for the selected store + month (this sum IS the
  // monthly target now that direct monthly entry is hidden).
  const loadDailyStatus = useCallback(async (locId, month) => {
    if (!locId) { setDailyStatus(null); return; }
    try {
      const res = await api.get('/eod-report/targets/daily', { params: { locationId: locId, month: (month || '') + '-01' } });
      setDailyStatus({ count: res.data.count || 0, total: res.data.total || 0 });
    } catch { setDailyStatus(null); }
  }, []);

  useEffect(() => { if (showTargetForm) loadDailyStatus(targetLocId, targetMonth); }, [showTargetForm, targetLocId, targetMonth, loadDailyStatus]);

  const deleteTarget = async (locId) => {
    if (!window.confirm('Delete this target?')) return;
    try {
      await api.delete(`/eod-report/targets/${locId}`, { params: { month: targetMonth + '-01' } });
      loadTargets(targetMonth);
      fetchReport();
    } catch (e) {
      alert('Failed to delete target: ' + (e.response?.data?.error || e.message));
    }
  };

  // Download a sample daily-target CSV for the selected month: Date column pre-filled with every
  // date of the month (YYYY-MM-DD), blank Target column for the manager to fill in.
  const downloadDailyTemplate = () => {
    const [y, m] = targetMonth.split('-').map(Number);
    const days = new Date(y, m, 0).getDate(); // last day of month
    const store = (data?.stores || allLocations).find(s => String(s.locationId || s.id) === String(targetLocId));
    const storeName = (store?.locationName || store?.name || 'store').replace(/ *\(Combined\)/i, '');
    let csv = 'Date,Target\n';
    for (let d = 1; d <= days; d++) csv += `${targetMonth}-${String(d).padStart(2, '0')},\n`;
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `daily-targets_${storeName}_${targetMonth}.csv`);
  };

  // Parse the uploaded CSV (Date,Target) and upsert it for the selected store.
  const uploadDailyCsv = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!targetLocId) { alert('Select a store first, then upload its daily CSV.'); return; }
    const store = (data?.stores || allLocations).find(s => String(s.locationId || s.id) === String(targetLocId));
    const storeLabel = (store?.locationName || store?.name || 'store').replace(/ *\(Combined\)/i, '');
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = String(ev.target.result || '');
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        // Drop a header row if the first cell isn't a date.
        if (lines.length && !/^\d/.test(lines[0])) lines.shift();
        const rows = lines.map(l => { const [date, target] = l.split(','); return { date: (date || '').trim(), target: (target || '').trim() }; })
                          .filter(r => r.date);
        if (!rows.length) { alert('No data rows found in the CSV.'); return; }
        setDailyBusy(true);
        const res = await api.post('/eod-report/targets/daily', {
          locationId: targetLocId,
          locationName: store?.locationName || store?.name || '',
          rows,
        });
        const s = res.data || {};
        const skipped = (s.skipped || []).length;
        alert(`Daily targets uploaded for ${storeLabel}:\n• Saved: ${s.saved || 0} day(s) (previous sheet for the month replaced)${skipped ? `\n• Skipped (bad date/target): ${skipped}` : ''}`);
        loadDailyStatus(targetLocId, targetMonth);
        fetchReport();
      } catch (err) {
        alert('Upload failed: ' + (err.response?.data?.error || err.message));
      } finally {
        setDailyBusy(false);
      }
    };
    reader.readAsText(file);
  };

  // Excel export for manager — with ExcelJS formatting
  const exportToExcel = async () => {
    if (!data || !data.stores) return;
    const stores = data.stores;
    const totals = data.totals;

    // FTD & MTD shown SIDE BY SIDE. Column groups = TOTAL + each store; each group has an FTD and an
    // MTD sub-column (categories get FTD qty/value/orders + MTD qty/value/orders). (Aug 2026)
    const groups = [{ name: 'TOTAL', ftd: totals.ftd, mtd: totals.mtd, reviews: totals.googleReviewsTotal }]
      .concat(stores.map(s => ({ name: (s.locationName || '').replace(/ *\(Combined\)/i, ''), ftd: s.ftd, mtd: s.mtd, reviews: s.googleReviewsTotal })));
    const simpleCols = 1 + groups.length * 2;
    const catCols = 1 + groups.length * 6;
    const maxCols = Math.max(simpleCols, catCols);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Region Report');
    const widths = [{ width: 34 }];
    for (let i = 1; i < maxCols; i++) widths.push({ width: 14 });
    ws.columns = widths;

    const borderThin = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
    const fillLightOrange = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3E0' } };
    const fillGreenHeader = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC8E6C9' } };
    const fillYellow = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE0B2' } };
    const fontBold = { bold: true, size: 11 };
    const alignCenter = { horizontal: 'center', vertical: 'middle' };
    const fmtV = (v, fmt) => (fmt === 'inr' ? formatINR(v) : fmt === 'pct' ? formatPct(v) : (v ?? 0));

    const style = (row, fill, font, cols) => {
      const n = cols || maxCols;
      for (let c = 1; c <= n; c++) {
        const cell = row.getCell(c);
        cell.border = borderThin;
        cell.alignment = c === 1 ? { vertical: 'middle' } : alignCenter;
        if (fill) cell.fill = fill;
        if (font) cell.font = font;
      }
    };
    const sectionTitle = (t) => {
      const r = ws.addRow([t]);
      ws.mergeCells(r.number, 1, r.number, maxCols);
      r.getCell(1).font = { bold: true, size: 12 }; r.getCell(1).fill = fillGreenHeader; r.getCell(1).alignment = { vertical: 'middle' };
    };
    // Two-row header for simple sections: group name (merged over its FTD+MTD), then FTD | MTD.
    const simpleHeader = (labelText) => {
      const names = [labelText]; groups.forEach(g => names.push(g.name, ''));
      const r1 = ws.addRow(names); let col = 2; groups.forEach(() => { ws.mergeCells(r1.number, col, r1.number, col + 1); col += 2; });
      style(r1, fillGreenHeader, fontBold, simpleCols);
      const sub = ['']; groups.forEach(() => sub.push('FTD', 'MTD'));
      style(ws.addRow(sub), fillYellow, fontBold, simpleCols);
    };
    // Metric row with FTD & MTD per group. ftdGet/mtdGet may be null → '—' (period not applicable).
    const row2 = (label, ftdGet, mtdGet, fmt, opts = {}) => {
      const vals = [label];
      groups.forEach(g => { vals.push(ftdGet ? fmtV(ftdGet(g.ftd), fmt) : '—'); vals.push(mtdGet ? fmtV(mtdGet(g.mtd), fmt) : '—'); });
      style(ws.addRow(vals), opts.fill, opts.bold ? fontBold : undefined, simpleCols);
    };
    // Non-period value shown identically under FTD & MTD.
    const rowSame = (label, get, fmt) => {
      const vals = [label]; groups.forEach(g => { const v = fmtV(get(g), fmt); vals.push(v, v); });
      style(ws.addRow(vals), undefined, undefined, simpleCols);
    };

    // Title
    const titleRow = ws.addRow(['REGION EOD REPORT']);
    ws.mergeCells(titleRow.number, 1, titleRow.number, maxCols);
    titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFE65100' } };
    titleRow.getCell(1).alignment = alignCenter; titleRow.getCell(1).fill = fillLightOrange;
    const dateRow = ws.addRow([`${data.reportDate}   ·   MTD Live Days: ${data.mtdLiveDays}`]);
    ws.mergeCells(dateRow.number, 1, dateRow.number, maxCols);
    dateRow.getCell(1).font = fontBold; dateRow.getCell(1).alignment = alignCenter;
    ws.addRow([]);

    // ===== SALES & PAYMENTS =====
    sectionTitle('SALES & PAYMENTS');
    simpleHeader('Metric');
    row2('Sales', f => f.salesFTD, m => m.salesMTD, 'inr', { bold: true });
    row2('Cash Collected', f => f.cashCollected, m => m.cashCollected, 'inr');
    row2('Online Payment', f => f.onlinePayment, m => m.onlinePayment, 'inr');
    row2('Snapmint Payment', f => f.snapmintPayment, m => m.snapmintPayment, 'inr');
    row2('Card Payment', f => f.cardPayment, m => m.cardPayment, 'inr');
    row2('UPI Payment', f => f.upiPayment, m => m.upiPayment, 'inr');
    row2('Invoice Count', f => f.invoiceCount, m => m.invoiceCount, '');
    row2('Quantity Sold', f => f.qtySold, m => m.qtySold, '');
    ws.addRow([]);

    // ===== TARGETS (MTD) =====
    sectionTitle('TARGETS');
    simpleHeader('Metric');
    row2('MTD Target', null, m => m.monthlyTarget, 'inr', { bold: true });
    row2('Achievement %', null, m => m.achievementPct, 'pct', { bold: true, fill: fillLightOrange });
    row2('Daily Run Rate', null, m => m.dailyRunRate, 'inr');
    row2('Required Daily Run Rate', null, m => (m.requiredDailyRunRate ? m.requiredDailyRunRate : '--'), '');
    ws.addRow([]);

    // ===== PRODUCT CATEGORIES (FTD | MTD side by side) =====
    sectionTitle('PRODUCT CATEGORIES');
    const cnames = ['Category']; groups.forEach(g => { for (let k = 0; k < 6; k++) cnames.push(k === 0 ? g.name : ''); });
    const cr1 = ws.addRow(cnames); { let col = 2; groups.forEach(() => { ws.mergeCells(cr1.number, col, cr1.number, col + 5); col += 6; }); }
    style(cr1, fillGreenHeader, fontBold, catCols);
    const cper = ['']; groups.forEach(() => cper.push('FTD', '', '', 'MTD', '', ''));
    const cr2 = ws.addRow(cper); { let col = 2; groups.forEach(() => { ws.mergeCells(cr2.number, col, cr2.number, col + 2); ws.mergeCells(cr2.number, col + 3, cr2.number, col + 5); col += 6; }); }
    style(cr2, fillGreenHeader, fontBold, catCols);
    const csub = ['Category']; groups.forEach(() => csub.push('Qty', 'Value (₹)', 'Orders', 'Qty', 'Value (₹)', 'Orders'));
    style(ws.addRow(csub), fillYellow, fontBold, catCols);
    PRODUCT_CATEGORIES.forEach(cat => {
      const vals = [cat];
      groups.forEach(g => {
        const f = g.ftd.categories?.[cat] || { qty: 0, value: 0, orders: 0 };
        const m = g.mtd.categories?.[cat] || { qty: 0, value: 0, orders: 0 };
        vals.push(f.qty || 0, formatINR(f.value || 0), f.orders || 0, m.qty || 0, formatINR(m.value || 0), m.orders || 0);
      });
      style(ws.addRow(vals), undefined, undefined, catCols);
    });
    const sumCats = (obj) => Object.values(obj || {}).reduce((a, c) => ({ qty: a.qty + (c.qty || 0), value: a.value + (c.value || 0), orders: a.orders + (c.orders || 0) }), { qty: 0, value: 0, orders: 0 });
    const ctot = ['📦 TOTAL'];
    groups.forEach(g => { const f = sumCats(g.ftd.categories); const m = sumCats(g.mtd.categories); ctot.push(f.qty, formatINR(f.value), f.orders, m.qty, formatINR(m.value), m.orders); });
    style(ws.addRow(ctot), fillYellow, fontBold, catCols);
    ws.addRow([]);

    // ===== WALK-INS & OPERATIONAL KPIs =====
    sectionTitle('WALK-INS & OPERATIONAL KPIs');
    simpleHeader('Metric');
    row2('Total Walk-ins', f => f.totalWalkins, m => m.totalWalkins, '', { bold: true });
    row2('Repeat Walk-ins', f => f.repeatWalkins, m => m.repeatWalkins, '');
    row2('No. of Buyers', f => (f.buyers != null ? f.buyers : f.invoiceCount), m => m.invoiceCount, '');
    row2('No. of Non Buyers', null, m => m.nonBuyers, '');
    row2('Conversion %', f => f.conversionPct, m => m.conversionPct, 'pct');
    row2('ABV', f => f.abv, m => m.abv, 'inr');
    row2('ASP', f => f.asp, m => m.asp, 'inr');
    row2('UPT', f => f.upt, m => m.upt, '');
    row2('Pop-in Calls', f => f.popinCalls, m => m.popinCalls, '');
    ws.addRow([]);

    // ===== CALLS, LEADS & REVIEWS =====
    sectionTitle('CALLS, LEADS & REVIEWS');
    simpleHeader('Metric');
    rowSame('Google Reviews (total)', g => g.reviews, '');
    row2('Incoming Calls', f => f.incomingCalls, m => m.incomingCalls, '');
    row2('Leads Captured', f => f.leadsCaptured, m => m.leadsCaptured, '');
    row2('Lifetime Open Leads', f => f.lifetimeOpenLeads, m => m.lifetimeOpenLeads, '');
    row2('MTD Open Leads', f => f.mtdOpenLeads, m => m.mtdOpenLeads, '');
    row2('Future Projected Business', f => f.futureProjectedBusiness, m => m.futureProjectedBusiness, 'inr');
    row2('Google Reviews Captured', f => f.googleReviewsCaptured, m => m.googleReviewsCaptured, '');
    row2('Google Reviews Responded', f => f.googleReviewsResponded, m => m.googleReviewsResponded, '');
    row2('Total Escalations', f => f.totalEscalations, m => m.totalEscalations, '');
    row2('Home Visits Done', f => f.homeVisitsDone, m => m.homeVisitsDone, '');

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Region_EOD_Report_${data.reportDate}.xlsx`);
  };

  if (loading) return <div className="eod-root" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="eod-loading-pulse" style={{ fontSize: 18, color: '#94a3b8', fontWeight: 600 }}>Loading Manager EOD Report...</div></div>;
  if (!data || !data.stores || data.stores.length === 0) return <div style={S.loading}>No stores found. Ensure locations are configured.</div>;

  const stores = data.stores;
  const totals = data.totals;
  const storeNames = stores.map(s => s.locationName);
  const period = activeTab; // 'ftd' or 'mtd'
  const periodData = totals[period] || {};

  // Summary cards data
  const summaryCards = activeTab === 'ftd' ? [
    { label: 'Sales FTD', value: formatINR(periodData.salesFTD), color: '#4f46e5', icon: '₹' },
    { label: 'Invoices', value: periodData.invoiceCount || 0, color: '#2563eb', icon: '#' },
    { label: 'Walk-ins', value: periodData.totalWalkins || 0, color: '#7c3aed', icon: '👥' },
    { label: 'Conversion', value: formatPct(periodData.conversionPct), color: '#059669', icon: '%' },
    { label: 'ABV', value: formatINR(periodData.abv), color: '#0d9488', icon: '₹' },
  ] : [
    { label: 'Sales MTD', value: formatINR(periodData.salesMTD), color: '#4f46e5', icon: '₹' },
    { label: 'Target', value: formatINR(periodData.monthlyTarget), color: '#2563eb', icon: '🎯' },
    { label: 'Achievement', value: formatPct(periodData.achievementPct), color: periodData.achievementPct >= 80 ? '#059669' : '#dc2626', icon: '%' },
    { label: 'Daily Run Rate', value: formatINR(periodData.dailyRunRate), color: '#7c3aed', icon: '📈' },
    { label: 'Invoices', value: periodData.invoiceCount || 0, color: '#0d9488', icon: '#' },
  ];

  return (
    <div className="eod-root eod-animate">
      <style>{EOD_CSS}</style>
      <Toast toast={toast} onClose={clearToast} />
      {/* Header Bar */}
      <div style={{ background: 'linear-gradient(135deg, #4338ca, #6366f1, #818cf8)', borderRadius: 14, padding: '18px 24px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, boxShadow: '0 4px 20px rgba(99,102,241,0.25)' }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: -0.3 }}>Region EOD Report</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="eod-input" style={{ ...S.input, background: 'rgba(255,255,255,0.95)', borderColor: 'transparent' }} />
          <select value={storeFilter} onChange={e => setStoreFilter(e.target.value)} className="eod-input" style={{ ...S.select, background: 'rgba(255,255,255,0.95)', borderColor: 'transparent', maxWidth: 220 }} title="Filter to a specific store">
            <option value="all">All Stores</option>
            {storeOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={locFilter} onChange={e => setLocFilter(e.target.value)} className="eod-input" style={{ ...S.select, background: 'rgba(255,255,255,0.95)', borderColor: 'transparent' }}>
            <option value="store">Store Only</option>
            <option value="warehouse">Warehouse Only</option>
            <option value="all">Store + Warehouse</option>
            <option value="combined">Combined (Store + WH)</option>
          </select>
          <button onClick={fetchReport} className="eod-btn" style={{ ...S.btn, background: '#fff', color: '#4f46e5', fontWeight: 800 }}>Refresh</button>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>Sync</span>
          <input type="date" value={syncFrom} onChange={e => { const v = e.target.value; setSyncFrom(v); if (syncTo && v > syncTo) setSyncTo(v); }} className="eod-input" style={{ ...S.input, background: 'rgba(255,255,255,0.95)', borderColor: 'transparent' }} title="Sync start date" />
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.9)' }}>→</span>
          <input type="date" value={syncTo} onChange={e => { const v = e.target.value; setSyncTo(v); if (syncFrom && v < syncFrom) setSyncFrom(v); }} className="eod-input" style={{ ...S.input, background: 'rgba(255,255,255,0.95)', borderColor: 'transparent' }} title="Sync end date (max 10 days)" />
          <button onClick={syncShopify} disabled={syncing} className="eod-btn" style={{ ...S.btn, background: '#0ea5e9', color: '#fff', fontWeight: 800, opacity: syncing ? 0.7 : 1 }} title="Fetch the selected date range of orders from Shopify (max 10 days)">{syncing ? 'Syncing…' : '🔄 Sync Shopify'}</button>
          <button onClick={() => setShowTargetForm(v => !v)} className="eod-btn" style={{ ...S.btn, background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)' }}>
            {showTargetForm ? '✕ Close Targets' : '🎯 Targets'}
          </button>
          <button onClick={exportToExcel} className="eod-btn" style={{ ...S.btn, background: '#fff', color: '#059669', fontWeight: 800 }}>📊 Export</button>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: 13 }}>{data.reportDate}</div>
      </div>

      {/* Target Management Panel */}
      {showTargetForm && (
        <div style={{ ...S.card, border: '1px solid #e0e7ff', overflow: 'visible' }}>
          <div style={{ background: 'linear-gradient(135deg, #2563eb, #3b82f6)', padding: '10px 20px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '14px 14px 0 0' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>🎯 Monthly Sales Targets</span>
            <input type="month" value={targetMonth} onChange={e => setTargetMonth(e.target.value)} className="eod-input" style={{ ...S.input, background: 'rgba(255,255,255,0.9)', fontSize: 12 }} />
          </div>
          <div style={{ padding: 16 }}>
            {/* Current monthly targets — the daily-sheet sum is the source of truth; legacy manual
                targets show only as a fallback (and can be removed). */}
            {existingTargets.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 16 }}>
                {existingTargets.map((t, i) => (
                  <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', background: '#fff', borderLeft: `4px solid ${t.source === 'daily' ? '#2563eb' : '#94a3b8'}` }}>
                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{(t.location_name || t.location_id || '').replace(/ *\(Combined\)/i, '')}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {t.source === 'daily' ? (
                        <button onClick={() => openTargetDetail(t)} title="Click to view the day-wise targets uploaded for this store"
                          style={{ fontSize: 22, fontWeight: 800, color: '#4f46e5', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 4, fontFamily: 'inherit' }}>
                          ₹{formatINR(Math.round(t.monthly_target))}
                        </button>
                      ) : (
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#4f46e5' }}>₹{formatINR(Math.round(t.monthly_target))}</div>
                      )}
                      {t.source === 'monthly' && (
                        <button onClick={() => deleteTarget(t.location_id)} style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 8, cursor: 'pointer', padding: '6px 10px', fontSize: 13, transition: 'background 0.2s' }} title="Remove legacy monthly target"
                          onMouseEnter={e => e.target.style.background = '#ffebee'} onMouseLeave={e => e.target.style.background = '#f5f5f5'}>🗑️</button>
                      )}
                    </div>
                    <div style={{ fontSize: 10.5, fontWeight: 600, marginTop: 6, color: t.source === 'daily' ? '#2563eb' : '#94a3b8' }}>
                      {t.source === 'daily' ? `📅 From daily sheet · ${t.days || 0} day(s) · click for day-wise` : 'Manual (legacy) — upload a daily sheet to override'}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Daily Targets (bulk sheet) — the ONLY way to set targets now. The sum of the sheet
                is the monthly target (used for MTD/achievement); FTD uses each date's value. */}
            <div style={{ background: '#eff6ff', borderRadius: 10, padding: '14px 16px', border: '1px dashed #93c5fd' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 8 }}>📅 Daily Targets (CSV) — the sheet total becomes the monthly target</div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ flex: 2, minWidth: 220 }}>
                  <label style={{ fontSize: 11, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 }}>Store</label>
                  <select value={targetLocId} onChange={e => setTargetLocId(e.target.value)} style={{ ...S.select, width: '100%', padding: '8px 12px', maxWidth: 'none' }}>
                    <option value="">Select store...</option>
                    {(data?.stores || allLocations).map(s => <option key={s.locationId || s.id} value={s.locationId || s.id}>{(s.locationName || s.name || '').replace(/ *\(Combined\)/i, '')}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: '#475569', marginBottom: 10, lineHeight: 1.5 }}>
                Pick a store, download the template for <strong>{targetMonth}</strong> (one row per date), fill the <strong>Target</strong> column, then upload it.
                The <strong>sum of the sheet is the monthly target</strong>; FTD uses each date's value. Re-uploading a sheet <strong>replaces</strong> that month.
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={downloadDailyTemplate} disabled={!targetLocId} style={{ ...S.btn, background: '#fff', color: '#2563eb', border: '1px solid #93c5fd', padding: '8px 16px', opacity: targetLocId ? 1 : 0.55, cursor: targetLocId ? 'pointer' : 'not-allowed' }}>⬇ Download Template</button>
                <label style={{ ...S.btn, background: (dailyBusy || !targetLocId) ? '#93c5fd' : '#2563eb', color: '#fff', padding: '8px 16px', cursor: (dailyBusy || !targetLocId) ? 'not-allowed' : 'pointer', display: 'inline-block', opacity: targetLocId ? 1 : 0.7 }}>
                  {dailyBusy ? 'Uploading…' : '⬆ Upload CSV'}
                  <input type="file" accept=".csv,text/csv" onChange={uploadDailyCsv} disabled={dailyBusy || !targetLocId} style={{ display: 'none' }} />
                </label>
                {targetLocId && dailyStatus && dailyStatus.count > 0 && (
                  <span style={{ fontSize: 11.5, color: '#065f46', fontWeight: 700 }}>Set for {targetMonth}: {dailyStatus.count} day(s) · monthly target ₹{formatINR(Math.round(dailyStatus.total))}</span>
                )}
                {targetLocId && dailyStatus && dailyStatus.count === 0 && (
                  <span style={{ fontSize: 11.5, color: '#b45309' }}>No sheet uploaded for {targetMonth} yet.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Day-wise targets modal — opens when a store's MTD price is clicked; shows exactly what was uploaded */}
      {targetDetail && (() => {
        const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        const rows = targetDetailRows || [];
        const dOf = (r) => String(r.target_date).slice(0, 10);
        const monthTotal = rows.reduce((s, r) => s + Number(r.daily_target || 0), 0);
        // Cumulative running total per day: day 1 = d1, day 2 = d1+d2, … (shown directly, not "20+20").
        let running = 0;
        const cumRows = rows.map(r => { running += Number(r.daily_target || 0); return { d: dOf(r), target: Number(r.daily_target || 0), cum: running }; });
        return (
          <div onClick={() => setTargetDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ background: 'linear-gradient(135deg, #2563eb, #3b82f6)', color: '#fff', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>{targetDetail.locationName}</div>
                  <div style={{ fontSize: 11.5, opacity: 0.9 }}>Day-wise targets · {targetMonth}</div>
                </div>
                <button onClick={() => setTargetDetail(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 16, width: 30, height: 30, lineHeight: '30px' }}>✕</button>
              </div>
              <div style={{ overflowY: 'auto', padding: '8px 0' }}>
                {targetDetailLoading ? (
                  <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Loading…</div>
                ) : rows.length === 0 ? (
                  <div style={{ padding: 30, textAlign: 'center', color: '#b45309' }}>No daily sheet uploaded for this store / month.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 18px', color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #e2e8f0' }}>Date</th>
                        <th style={{ textAlign: 'right', padding: '8px 18px', color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #e2e8f0' }}>Target</th>
                        <th style={{ textAlign: 'right', padding: '8px 18px', color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #e2e8f0' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cumRows.map((r, i) => {
                        const isToday = r.d === todayStr;
                        const elapsed = r.d <= todayStr;
                        return (
                          <tr key={i} style={{ background: isToday ? '#eff6ff' : 'transparent' }}>
                            <td style={{ padding: '7px 18px', borderBottom: '1px solid #f1f5f9', color: elapsed ? '#0f172a' : '#94a3b8', fontWeight: isToday ? 800 : 500 }}>
                              {new Date(r.d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}{isToday ? ' · Today' : ''}
                            </td>
                            <td style={{ padding: '7px 18px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: elapsed ? '#4f46e5' : '#94a3b8', fontWeight: 600 }}>
                              ₹{formatINR(Math.round(r.target))}
                            </td>
                            <td style={{ padding: '7px 18px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: elapsed ? '#0f172a' : '#94a3b8', fontWeight: 800 }}>
                              ₹{formatINR(Math.round(r.cum))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {!targetDetailLoading && rows.length > 0 && (
                <div style={{ borderTop: '2px solid #e2e8f0', padding: '12px 18px', display: 'flex', justifyContent: 'flex-end', background: '#f8fafc' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Full month ({rows.length} days)</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#4f46e5' }}>₹{formatINR(Math.round(monthTotal))}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        {summaryCards.map((c, i) => (
          <div key={i} className="eod-stat-card" style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)', borderLeft: `4px solid ${c.color}`, border: '1px solid rgba(0,0,0,0.04)', borderLeftWidth: 4, borderLeftColor: c.color }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color, letterSpacing: -0.5 }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* FTD / MTD Tab Switcher */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#f1f5f9', borderRadius: 14, padding: 3 }}>
        {['ftd', 'mtd'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className="eod-tab"
            style={{ flex: 1, padding: '12px 0', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 1,
              background: activeTab === tab ? (tab === 'ftd' ? 'linear-gradient(135deg, #4f46e5, #6366f1)' : 'linear-gradient(135deg, #059669, #10b981)') : 'transparent',
              color: activeTab === tab ? '#fff' : '#94a3b8',
              borderRadius: 12,
              transition: 'all 0.25s ease',
            }}>
            {tab === 'ftd' ? '📅 Today (FTD)' : '📆 Month (MTD)'}
          </button>
        ))}
      </div>

      {/* FTD Target/Achievement Bar (only in FTD tab) */}
      {activeTab === 'ftd' && (
        <div style={{ ...S.card, marginBottom: 12 }}>
          <div style={S.sectionHeader} className="eod-section-header">Target & Achievement</div>
          <div style={{ overflowX: 'auto' }} className="eod-scroll">
            <table style={S.table} className="eod-table">
              <thead>
                <tr><th style={S.thLeft}>Metric</th><th style={S.th}>Total</th>{storeNames.map(n => <th key={n} style={S.th}>{n}</th>)}</tr>
              </thead>
              <tbody>
                <FTDRow label="FTD Target" total={formatINR(periodData.ftdTarget)} stores={stores} getter={s => formatINR(s.ftd.ftdTarget)} bold />
                <FTDRow label="Sales FTD" total={formatINR(periodData.salesFTD)} stores={stores} getter={s => formatINR(s.ftd.salesFTD)} bold />
                <FTDRow label="Achievement %" total={formatPct(periodData.achievementPct)} stores={stores} getter={s => formatPct(s.ftd.achievementPct)} highlight />
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MTD Target/Achievement Bar (only in MTD tab) */}
      {activeTab === 'mtd' && (
        <div style={{ ...S.card, marginBottom: 12 }}>
          <div style={S.sectionHeaderGreen} className="eod-section-header">Target & Achievement</div>
          <div style={{ overflowX: 'auto' }} className="eod-scroll">
            <table style={S.table} className="eod-table">
              <thead>
                <tr><th style={S.thLeft}>Metric</th><th style={S.th}>Total</th>{storeNames.map(n => <th key={n} style={S.th}>{n}</th>)}</tr>
              </thead>
              <tbody>
                <FTDRow label="MTD Target" total={formatINR(periodData.monthlyTarget)} stores={stores} getter={s => formatINR(s.mtd.monthlyTarget)} bold />
                <FTDRow label="Sales MTD" total={formatINR(periodData.salesMTD)} stores={stores} getter={s => formatINR(s.mtd.salesMTD)} bold />
                <FTDRow label="Achievement %" total={formatPct(periodData.achievementPct)} stores={stores} getter={s => formatPct(s.mtd.achievementPct)} highlight />
                <FTDRow label="Daily Run Rate" total={formatINR(periodData.dailyRunRate)} stores={stores} getter={s => formatINR(s.mtd.dailyRunRate)} />
                <FTDRow label="Req. Daily Run Rate" total={periodData.requiredDailyRunRate ? formatINR(periodData.requiredDailyRunRate) : '--'} stores={stores} getter={s => s.mtd.requiredDailyRunRate ? formatINR(s.mtd.requiredDailyRunRate) : '--'} />
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Google Reviews */}
      <div style={{ ...S.card, marginBottom: 12 }}>
        <div style={S.sectionHeaderPurple} className="eod-section-header">Google Reviews</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '10px 20px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#64748b' }}>Total:</span>
          <span style={{ fontWeight: 800, fontSize: 18, color: '#7c3aed' }}>{totals.googleReviewsTotal}</span>
          {stores.map(s => (
            <span key={s.locationId} style={{ fontSize: 12, color: '#666' }}>
              <strong>{s.locationName.replace('HO - ', '').replace(' Location', '')}:</strong> {s.googleReviewsTotal}
            </span>
          ))}
        </div>
      </div>

      {/* Sales & Payments */}
      <div style={S.card}>
        <div style={S.sectionHeader} className="eod-section-header">Sales & Payments</div>
        <div style={{ overflowX: 'auto' }} className="eod-scroll">
          <table style={S.table} className="eod-table">
            <thead>
              <tr><th style={S.thLeft}>Metric</th><th style={S.th}>Total</th>{storeNames.map(n => <th key={n} style={S.th}>{n}</th>)}</tr>
            </thead>
            <tbody>
              <FTDRow label={activeTab === 'ftd' ? 'Sales FTD' : 'Sales MTD'} total={formatINR(activeTab === 'ftd' ? periodData.salesFTD : periodData.salesMTD)} stores={stores} getter={s => formatINR(activeTab === 'ftd' ? s.ftd.salesFTD : s.mtd.salesMTD)} bold />
              <FTDRow label="Cash Collected" total={formatINR(periodData.cashCollected)} stores={stores} getter={s => formatINR(s[period].cashCollected)} />
              <FTDRow label="Online Payment" total={formatINR(periodData.onlinePayment)} stores={stores} getter={s => formatINR(s[period].onlinePayment)} />
              <FTDRow label="Snapmint Payment" total={formatINR(periodData.snapmintPayment)} stores={stores} getter={s => formatINR(s[period].snapmintPayment)} />
              <FTDRow label="Card Payment" total={formatINR(periodData.cardPayment)} stores={stores} getter={s => formatINR(s[period].cardPayment)} />
              <FTDRow label="UPI Payment" total={formatINR(periodData.upiPayment)} stores={stores} getter={s => formatINR(s[period].upiPayment)} />
              <FTDRow label="Invoice Count" total={periodData.invoiceCount} stores={stores} getter={s => s[period].invoiceCount} />
              <FTDRow label="Quantity Sold" total={periodData.qtySold} stores={stores} getter={s => s[period].qtySold} />
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Categories */}
      <div style={S.card}>
        <div style={S.sectionHeaderTeal} className="eod-section-header">Product Categories — {activeTab === 'ftd' ? 'FTD' : 'MTD'}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...S.table, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...S.thLeft, verticalAlign: 'bottom', borderBottom: '2px solid #ddd' }}>Category</th>
                <th colSpan={3} style={{ ...S.th, background: '#e0f2f1', color: '#00695c', borderBottom: '1px solid #80cbc4', fontSize: 12, fontWeight: 800 }}>TOTAL</th>
                {storeNames.map(n => <th key={n} colSpan={3} style={{ ...S.th, borderBottom: '1px solid #ddd', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{n}</th>)}
              </tr>
              <tr>
                <th style={{ ...S.th, background: '#e0f7fa', color: '#00695c', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderBottom: '2px solid #00695c' }}>Qty</th>
                <th style={{ ...S.th, background: '#e0f7fa', color: '#00695c', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderBottom: '2px solid #00695c' }}>Value (₹)</th>
                <th style={{ ...S.th, background: '#e0f7fa', color: '#00695c', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderBottom: '2px solid #00695c' }}>Orders</th>
                {storeNames.map(n => <React.Fragment key={n}><th style={{ ...S.th, fontSize: 10, fontWeight: 700, color: '#666', padding: '4px 8px', borderBottom: '2px solid #ddd' }}>Qty</th><th style={{ ...S.th, fontSize: 10, fontWeight: 700, color: '#666', padding: '4px 8px', borderBottom: '2px solid #ddd' }}>Value (₹)</th><th style={{ ...S.th, fontSize: 10, fontWeight: 700, color: '#1565c0', padding: '4px 8px', borderBottom: '2px solid #ddd' }}>Orders</th></React.Fragment>)}
              </tr>
            </thead>
            <tbody>
              {PRODUCT_CATEGORIES.map(cat => {
                const tc = periodData.categories?.[cat] || { qty: 0, value: 0, orders: 0 };
                const hasData = tc.qty > 0;
                return (
                <tr key={cat} style={{ background: hasData ? '#fff' : '#fafafa' }}>
                  <td style={{ ...S.tdLeft, fontWeight: 600, fontSize: 12 }}>{cat}</td>
                  <td style={{ ...S.td, fontWeight: 700, fontSize: 12, textAlign: 'center', color: hasData ? '#333' : '#ccc' }}>{tc.qty}</td>
                  <td style={{ ...S.td, fontWeight: 600, fontSize: 12, textAlign: 'center', color: hasData ? '#2e7d32' : '#ccc' }}>{formatINR(tc.value)}</td>
                  <td style={{ ...S.td, fontWeight: 600, fontSize: 12, textAlign: 'center', color: hasData ? '#1565c0' : '#ccc' }}>{tc.orders || 0}</td>
                  {stores.map(s => {
                    const c = s[period].categories?.[cat] || { qty: 0, value: 0, orders: 0 };
                    const ld = c.qty > 0;
                    return <React.Fragment key={s.locationId}><td style={{ ...S.td, fontWeight: 700, fontSize: 12, textAlign: 'center', color: ld ? '#333' : '#ccc' }}>{c.qty}</td><td style={{ ...S.td, fontWeight: 600, fontSize: 12, textAlign: 'center', color: ld ? '#2e7d32' : '#ccc' }}>{formatINR(c.value)}</td><td style={{ ...S.td, fontWeight: 600, fontSize: 12, textAlign: 'center', color: ld ? '#1565c0' : '#ccc' }}>{c.orders || 0}</td></React.Fragment>;
                  })}
                </tr>
                );
              })}
              {(() => {
                // TOTAL row must tally with the category column above it, so sum the categories
                // (NOT salesFTD/qtySold/invoiceCount, which are net-revenue / raw-qty / distinct-orders
                // and legitimately differ from the gross category sums). Matches the per-store columns.
                const ct = Object.values(periodData.categories || {}).reduce((a, c) => ({ qty: a.qty + (c.qty || 0), value: a.value + (c.value || 0), orders: a.orders + (c.orders || 0) }), { qty: 0, value: 0, orders: 0 });
                return (
                <tr style={{ ...S.totalRow, borderTop: '2px solid #00695c' }}>
                  <td style={{ ...S.tdLeft, fontWeight: 700 }}>📦 TOTAL</td>
                  <td style={{ ...S.tdBold, fontSize: 13 }}>{ct.qty}</td>
                  <td style={{ ...S.tdBold, fontSize: 13, color: '#2e7d32' }}>{formatINR(ct.value)}</td>
                  <td style={{ ...S.tdBold, fontSize: 13, color: '#1565c0' }}>{ct.orders}</td>
                  {stores.map(s => {
                    const t = Object.values(s[period].categories || {}).reduce((a, c) => ({ qty: a.qty + (c.qty || 0), value: a.value + (c.value || 0), orders: a.orders + (c.orders || 0) }), { qty: 0, value: 0, orders: 0 });
                    return <React.Fragment key={s.locationId}><td style={S.tdBold}>{t.qty}</td><td style={{ ...S.tdBold, color: '#2e7d32' }}>{formatINR(t.value)}</td><td style={{ ...S.tdBold, color: '#1565c0' }}>{t.orders}</td></React.Fragment>;
                  })}
                </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Walk-ins & Conversion */}
      <div style={S.card}>
        <div style={S.sectionHeaderBlue} className="eod-section-header">Walk-ins & Conversion</div>
        <div style={{ overflowX: 'auto' }} className="eod-scroll">
          <table style={S.table} className="eod-table">
            <thead>
              <tr><th style={S.thLeft}>Metric</th><th style={S.th}>Total</th>{storeNames.map(n => <th key={n} style={S.th}>{n}</th>)}</tr>
            </thead>
            <tbody>
              <FTDRow label="Total Walk-ins" total={periodData.totalWalkins} stores={stores} getter={s => s[period].totalWalkins} bold />
              <FTDRow label="Repeat Walk-ins" total={periodData.repeatWalkins} stores={stores} getter={s => s[period].repeatWalkins} />
              <FTDRow label="No. of Buyers" total={periodData.invoiceCount} stores={stores} getter={s => s[period].invoiceCount} />
              {activeTab === 'mtd' && <FTDRow label="No. of Non Buyers" total={periodData.nonBuyers} stores={stores} getter={s => s.mtd.nonBuyers} />}
              <FTDRow label="Conversion %" total={formatPct(periodData.conversionPct)} stores={stores} getter={s => formatPct(s[period].conversionPct)} highlight />
              <FTDRow label="ABV" total={formatINR(periodData.abv)} stores={stores} getter={s => formatINR(s[period].abv)} />
              <FTDRow label="ASP" total={formatINR(periodData.asp)} stores={stores} getter={s => formatINR(s[period].asp)} />
              <FTDRow label="UPT" total={periodData.upt} stores={stores} getter={s => s[period].upt} />
            </tbody>
          </table>
        </div>
      </div>

      {/* Calls, Leads & Reviews */}
      <div style={S.card}>
        <div style={{ background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)', color: '#fff', padding: '11px 18px', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }} className="eod-section-header">Calls, Leads & Reviews</div>
        <div style={{ overflowX: 'auto' }} className="eod-scroll">
          <table style={S.table} className="eod-table">
            <thead>
              <tr><th style={S.thLeft}>Metric</th><th style={S.th}>Total</th>{storeNames.map(n => <th key={n} style={S.th}>{n}</th>)}</tr>
            </thead>
            <tbody>
              <FTDRow label="Incoming Calls" total={periodData.incomingCalls} stores={stores} getter={s => s[period].incomingCalls} />
              {activeTab === 'ftd' && <FTDRow label="Pop-in Calls" total={periodData.popinCalls} stores={stores} getter={s => s.ftd.popinCalls} />}
              {activeTab === 'mtd' && <FTDRow label="Pop-in Calls" total={periodData.popinCalls} stores={stores} getter={s => s.mtd.popinCalls} />}
              <FTDRow label="Leads Captured" total={periodData.leadsCaptured} stores={stores} getter={s => s[period].leadsCaptured} />
              {activeTab === 'ftd' && <FTDRow label="Lifetime Open Leads" total={periodData.lifetimeOpenLeads} stores={stores} getter={s => s.ftd.lifetimeOpenLeads} />}
              <FTDRow label="MTD Open Leads" total={periodData.mtdOpenLeads} stores={stores} getter={s => s[period].mtdOpenLeads} />
              <FTDRow label="Future Projected Business" total={formatINR(periodData.futureProjectedBusiness)} stores={stores} getter={s => formatINR(s[period].futureProjectedBusiness)} />
              <FTDRow label="Google Reviews Captured" total={periodData.googleReviewsCaptured} stores={stores} getter={s => s[period].googleReviewsCaptured} />
              <FTDRow label="Google Reviews Responded" total={periodData.googleReviewsResponded} stores={stores} getter={s => s[period].googleReviewsResponded} />
            </tbody>
          </table>
        </div>
      </div>

      {/* Escalations & Home Visits */}
      <div style={S.card}>
        <div style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)', color: '#fff', padding: '11px 18px', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }} className="eod-section-header">Escalations & Home Visits</div>
        <div style={{ overflowX: 'auto' }} className="eod-scroll">
          <table style={S.table} className="eod-table">
            <thead>
              <tr><th style={S.thLeft}>Metric</th><th style={S.th}>Total</th>{storeNames.map(n => <th key={n} style={S.th}>{n}</th>)}</tr>
            </thead>
            <tbody>
              <FTDRow label="Total Escalations" total={periodData.totalEscalations} stores={stores} getter={s => s[period].totalEscalations} />
              <FTDRow label="Open Escalations" total={periodData.openEscalations} stores={stores} getter={s => s[period].openEscalations} />
              <FTDRow label="Home Visits Done" total={periodData.homeVisitsDone} stores={stores} getter={s => s[period].homeVisitsDone} />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DAILY KPI ENTRY FORM (Staff enters daily KPIs per location)
// ============================================================================
function KPIEntryForm({ kpiForm, setKpiForm, onSave, onCancel, saving }) {
  const field = (key, label, type = 'number') => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <label style={{ flex: '0 0 220px', fontSize: 13, fontWeight: 600 }}>{label}</label>
      <input
        type={type}
        value={kpiForm[key] || (type === 'number' ? 0 : '')}
        onChange={e => setKpiForm({ ...kpiForm, [key]: type === 'number' ? Number(e.target.value) : e.target.value })}
        style={{ ...S.input, width: 120 }}
        min={0}
      />
    </div>
  );

  return (
    <div style={{ ...S.card, padding: 20, border: '2px solid #6366f1' }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#4f46e5' }}>Enter Daily KPIs</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: '#2563eb' }}>Walk-ins</div>
          {field('new_walkins', 'New Walk-ins')}
          {field('repeat_walkins', 'Repeat Walk-ins')}
          {field('other_walkins', 'Other Walk-ins')}

          <div style={{ fontWeight: 700, fontSize: 13, margin: '12px 0 6px', color: '#2563eb' }}>Non-Buyers</div>
          {field('non_buyer_quotation', 'Non Buyer Quotation Shared')}
          {field('non_buyer_callback', 'Non Buyer Call Back')}
          {field('non_buyer_uninterested', 'Non Buyer Uninterested')}

          <div style={{ fontWeight: 700, fontSize: 13, margin: '12px 0 6px', color: '#2563eb' }}>Quotations</div>
          {field('quotation_shared_qty', 'Quotation Shared Qty')}
          {field('quotation_value', 'Quotation Value')}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: '#2563eb' }}>Google Reviews</div>
          {field('google_reviews_captured', 'Reviews Captured')}
          {field('google_reviews_responded', 'Reviews Responded')}

          <div style={{ fontWeight: 700, fontSize: 13, margin: '12px 0 6px', color: '#2563eb' }}>Calls & Leads</div>
          {field('incoming_calls', 'Incoming Calls')}
          {field('popin_calls', 'Pop-in Calls')}
          {field('leads_captured', 'Leads Captured')}
          {field('mtd_open_leads', 'MTD Open Leads')}
          {field('future_projected_business', 'Future Projected Business')}

          <div style={{ fontWeight: 700, fontSize: 13, margin: '12px 0 6px', color: '#2563eb' }}>Escalations & Visits</div>
          {field('total_escalations', 'Total Escalations')}
          {field('open_escalations', 'Open Escalations')}
          {field('home_visits_done', 'Home Visits Done')}
        </div>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button onClick={onSave} disabled={saving} style={{ ...S.btn, ...S.btnGreen }}>{saving ? 'Saving...' : 'Save KPIs'}</button>
        <button onClick={onCancel} style={{ ...S.btn, background: '#eee', color: '#333' }}>Cancel</button>
      </div>
    </div>
  );
}

// ============================================================================
// TABLE ROW HELPERS
// ============================================================================
function FTDRow({ label, total, stores, field, getter, bold, highlight, isTop }) {
  const getVal = getter || ((s) => (field ? s[field] : ''));

  return (
    <tr style={highlight ? { background: '#e8f5e9' } : bold ? S.totalRow : {}}>
      <td style={{ ...S.tdLeft, fontWeight: bold || isTop ? 700 : 600 }}>{label}</td>
      <td style={S.tdBold}>{total}</td>
      {stores.map(s => <td key={s.locationId} style={S.td}>{getVal(s)}</td>)}
    </tr>
  );
}

// ============================================================================
// MAIN EOD REPORT PAGE
// ============================================================================
function EODReport() {
  const { user } = useAuthStore();
  const roles = user?.roles || [];
  // super_admin is a superset of manager — it must see the full manager (Region) view, incl. Targets.
  const isManager = roles.includes('manager') || roles.includes('super_admin');

  if (isManager) {
    return <ManagerEODReport />;
  }

  return <StaffEODReport />;
}

export default EODReport;
